// src/pages/envio-bultos/EnvioBultos.js
import React, { useEffect, useState, useMemo } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import Badge from 'react-bootstrap/Badge'
import Spinner from 'react-bootstrap/Spinner'
import Alert from 'react-bootstrap/Alert'
import Table from 'react-bootstrap/Table'
import { toast } from 'react-toastify'
import api from 'api/api'

import TopHeader from 'components/TopHeader'
import { CATEGORIAS_TAMANO_COLORES } from 'config/envio'

function EnvioBultos() {
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(null) // ID de la categoría que se está guardando
	const [categorias, setCategorias] = useState([])

	// Cargar categorías
	const refreshCategorias = async () => {
		try {
			setLoading(true)
			const { datos } = await api.get('a/envio-categorias')
			setCategorias(datos || [])
		} catch (error) {
			toast.error('Error al cargar categorías de envío')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	// Actualizar categoría
	const actualizarCategoria = async (id, campo, valor) => {
		try {
			setSaving(id)
			const data = { [campo]: valor }
			await api.put(`a/envio-categorias/${id}`, data)

			// Actualizar estado local
			setCategorias(prev => prev.map(cat =>
				cat.id === id ? { ...cat, [campo]: valor } : cat
			))

			toast.success('Categoría actualizada')
		} catch (error) {
			toast.error('Error al actualizar categoría')
			console.error(error)
		} finally {
			setSaving(null)
		}
	}

	// Manejar cambio de unidades por bulto
	const handleUnidadesChange = (id, valor) => {
		// Permitir vacío y valores intermedios mientras se escribe (ej: "0", "0.", "0.4")
		setCategorias(prev => prev.map(cat =>
			cat.id === id ? { ...cat, unidadesPorBulto: valor } : cat
		))
	}

	// Guardar cuando pierde el foco
	const handleUnidadesBlur = (id, valor) => {
		const numValor = parseFloat(valor)
		if (isNaN(numValor) || numValor <= 0) {
			// Resetear a 1 si el valor no es válido
			actualizarCategoria(id, 'unidadesPorBulto', 1)
			return
		}
		actualizarCategoria(id, 'unidadesPorBulto', numValor)
	}

	useEffect(() => {
		refreshCategorias()
	}, [])

	// Obtener el badge de color para una categoría
	const getBadgeStyle = (codigo) => ({
		backgroundColor: CATEGORIAS_TAMANO_COLORES[codigo] || '#6c757d',
		color: ['M', 'PP'].includes(codigo) ? '#000' : '#fff'
	})

	// Helpers para ejemplos de cálculo
	const getCatVal = (cod) => {
		const c = categorias.find(x => x.codigo === cod)
		return c ? Number(c.unidadesPorBulto) : 1
	}
	const frac = (cod) => 1 / getCatVal(cod)
	const fracStr = (cod) => frac(cod).toFixed(2)
	const bultosTxt = (n) => `${n} bulto${n !== 1 ? 's' : ''}`

	return (
		<Container fluid id="envio-bultos" className="vh-100">
			<TopHeader
				iconName="box"
				text="Envío por Bultos"
			/>

			{/* Explicación del sistema */}
			<Row className="mb-4">
				<Col lg={8}>
					<Alert variant="info">
						<Alert.Heading>
							<i className="fa fa-info-circle me-2"></i>
							Sistema de Cálculo por Bultos
						</Alert.Heading>
						<p className="mb-2">
							Cada producto ocupa una parte del bulto según su tamaño. Por ejemplo, si configurás que caben 4 productos pequeños en un bulto, cada uno ocupa <strong>1/4 de bulto</strong>.
						</p>
						<p className="mb-2">
							Al sumar todas las partes se redondea hacia arriba. Ejemplo: 1/4 + 1/2 = 0.75 → <strong>1 bulto</strong>.
						</p>
						<p className="mb-2">
							<strong>Costo de envío = Precio del área × Total de bultos</strong>
						</p>
						<hr />
						<p className="mb-2">
							Configurá cuántas unidades de cada tamaño caben en un bulto. Los productos sin categoría cuentan como 1 bulto cada uno.
						</p>
						<p className="mb-0">
							<strong>Switch:</strong> Si desactivás una categoría, los productos asignados a ella se tratarán como si no tuvieran categoría (1 bulto cada uno).
						</p>
					</Alert>
				</Col>
			</Row>

			{/* Cards de categorías */}
			<Row>
				{loading ? (
					<Col className="text-center py-5">
						<Spinner animation="border" variant="primary" />
						<p className="mt-3 text-muted">Cargando categorías...</p>
					</Col>
				) : (
					categorias.map(cat => (
						<Col lg={4} md={6} key={cat.id} className="mb-4">
							<Card className={`h-100 ${!cat.activo ? 'opacity-50' : ''}`}>
								<Card.Header className="d-flex justify-content-between align-items-center">
									<div>
										<Badge style={getBadgeStyle(cat.codigo)} className="me-2">
											{cat.codigo}
										</Badge>
										<strong>{cat.nombre}</strong>
									</div>
									<Form.Check
										type="switch"
										id={`switch-${cat.id}`}
										checked={cat.activo === 1}
										onChange={(e) => actualizarCategoria(cat.id, 'activo', e.target.checked)}
										disabled={saving === cat.id}
										title={cat.activo === 1 ? 'Clic para desactivar categoría' : 'Clic para activar categoría'}
									/>
								</Card.Header>
								<Card.Body>
									<Form.Group>
										<Form.Label>Unidades por bulto</Form.Label>
										<div className="d-flex align-items-center">
											<Form.Control
												type="number"
												min="0.0001"
												step="any"
												value={cat.unidadesPorBulto}
												onChange={(e) => handleUnidadesChange(cat.id, e.target.value)}
												onBlur={(e) => handleUnidadesBlur(cat.id, e.target.value)}
												disabled={saving === cat.id}
												style={{ width: '100px' }}
											/>
											{saving === cat.id && (
												<Spinner animation="border" size="sm" className="ms-2" />
											)}
										</div>
										<Form.Text className="text-muted">
											{Number(cat.unidadesPorBulto) === 1
												? '1 unidad = 1 bulto'
												: Number(cat.unidadesPorBulto) >= 1
													? `Cada ${cat.unidadesPorBulto} unidades = 1 bulto`
													: `1 unidad = ${(1 / Number(cat.unidadesPorBulto)).toFixed(1)} bultos`
											}
										</Form.Text>
										<Form.Text className="text-muted d-block mt-1">
											<i className="fa fa-info-circle me-1"></i>
											Se guarda automáticamente al salir del campo
										</Form.Text>
									</Form.Group>
								</Card.Body>
								<Card.Footer className="text-muted small">
									<i className="fa fa-lightbulb me-1"></i>
									{Number(cat.unidadesPorBulto) >= 1
										? `${Number(cat.unidadesPorBulto)} ${cat.codigo} = 1 bulto`
										: `1 ${cat.codigo} ≈ ${Math.ceil(1 / Number(cat.unidadesPorBulto))} bultos`
									}
								</Card.Footer>
							</Card>
						</Col>
					))
				)}
			</Row>

			{/* Tabla de equivalencias */}
			{!loading && categorias.length > 0 && (() => {
				const calcularSobrante = (cat) => {
					const val = Number(cat.unidadesPorBulto)
					if (val <= 0) return null
					const fraccion = 1 / val
					const bultos = Math.ceil(fraccion)
					const sobrante = Math.round((bultos - fraccion) * 10000) / 10000

					if (sobrante <= 0.0001) return { fraccion, bultos, sobrante: 0, caben: [] }

					const caben = []
					const categoriasChicas = categorias
						.filter(c => Number(c.unidadesPorBulto) >= 1 && c.codigo !== cat.codigo)
						.sort((a, b) => Number(a.unidadesPorBulto) - Number(b.unidadesPorBulto))

					for (const chica of categoriasChicas) {
						const fracChica = 1 / Number(chica.unidadesPorBulto)
						const cantCabe = Math.floor((sobrante + 0.0001) / fracChica)
						if (cantCabe > 0) {
							caben.push({ codigo: chica.codigo, cantidad: cantCabe })
						}
					}

					return { fraccion, bultos, sobrante, caben }
				}

				return (
					<Row className="mt-4">
						<Col lg={12}>
							<Card>
								<Card.Header>
									<i className="fa fa-table me-2"></i>
									Tabla de Equivalencias
								</Card.Header>
								<Card.Body>
									<Table striped bordered hover responsive>
										<thead>
											<tr>
												<th>Categoría</th>
												<th>uds/Bulto</th>
												<th>1 item = fracción</th>
												<th>Bultos</th>
												<th>Sobrante</th>
												<th>¿Qué entra en el sobrante?</th>
											</tr>
										</thead>
										<tbody>
											{categorias.map(cat => {
												const datos = calcularSobrante(cat)
												if (!datos) return null
												return (
													<tr key={cat.id}>
														<td>
															<Badge style={getBadgeStyle(cat.codigo)} className="me-1">
																{cat.codigo}
															</Badge>
															{cat.nombre}
														</td>
														<td>{cat.unidadesPorBulto}</td>
														<td>{datos.fraccion.toFixed(3)}</td>
														<td><strong>{datos.bultos}</strong></td>
														<td>{datos.sobrante > 0 ? datos.sobrante.toFixed(3) : '—'}</td>
														<td>
															{datos.sobrante <= 0 ? (
																<span className="text-muted">Sin sobrante</span>
															) : datos.caben.length === 0 ? (
																<span className="text-muted">Nada cabe</span>
															) : (
																datos.caben.map((c, i) => (
																	<span key={c.codigo}>
																		{i > 0 && ', '}
																		{c.cantidad} <Badge style={getBadgeStyle(c.codigo)} className="me-1">{c.codigo}</Badge>
																	</span>
																))
															)}
														</td>
													</tr>
												)
											})}
										</tbody>
									</Table>
									<p className="text-muted mb-0 small">
										<i className="fa fa-info-circle me-1"></i>
										El sobrante es el espacio libre dentro de los bultos asignados. Se muestra la cantidad <strong>máxima individual</strong> de cada categoría que entra en ese espacio (no sumadas entre sí).
									</p>
								</Card.Body>
							</Card>
						</Col>
					</Row>
				)
			})()}

			{/* Ejemplos de cálculo */}
			{!loading && categorias.length > 0 && (() => {
				const ej1Fracs = frac('G') + frac('PP')
				const ej1Bultos = Math.ceil(ej1Fracs)
				const ej2Fracs = frac('G') + frac('M')
				const ej2Bultos = Math.ceil(ej2Fracs)
				const ej3Fracs = frac('GG') + frac('M') + frac('PPP')
				const ej3Bultos = Math.ceil(ej3Fracs)

				return (
					<Row className="mt-4">
						<Col lg={12}>
							<Card>
								<Card.Header>
									<i className="fa fa-calculator me-2"></i>
									Ejemplos de Cálculo
								</Card.Header>
								<Card.Body>
									<p className="text-muted mb-4">
										Con la configuración actual: {categorias.map(cat => `${cat.codigo}=${cat.unidadesPorBulto} uds/bulto`).join(', ')}
									</p>

									<Row>
										{/* Ejemplo 1: 1 G + 1 PP */}
										<Col md={4} className="mb-3">
											<Card className="h-100 border-primary">
												<Card.Header className="bg-primary text-white">
													<strong>Ejemplo 1:</strong> G + PP
												</Card.Header>
												<Card.Body>
													<p><strong>Carrito:</strong></p>
													<ul className="mb-3">
														<li>1 producto <Badge style={getBadgeStyle('G')}>G</Badge></li>
														<li>1 producto <Badge style={getBadgeStyle('PP')}>PP</Badge></li>
													</ul>
													<p><strong>Cálculo:</strong></p>
													<ul className="mb-3">
														<li>G: 1/{getCatVal('G')} = <strong>{fracStr('G')}</strong></li>
														<li>PP: 1/{getCatVal('PP')} = <strong>{fracStr('PP')}</strong></li>
													</ul>
													<p className="mb-2">Fracción total: {ej1Fracs.toFixed(2)} → ceil = <strong>{bultosTxt(ej1Bultos)}</strong></p>
													<Alert variant="info" className="mb-0 py-2">
														<strong>Total: {bultosTxt(ej1Bultos)}</strong>
													</Alert>
												</Card.Body>
											</Card>
										</Col>

										{/* Ejemplo 2: 1 G + 1 M */}
										<Col md={4} className="mb-3">
											<Card className="h-100 border-warning">
												<Card.Header className="bg-warning">
													<strong>Ejemplo 2:</strong> G + M
												</Card.Header>
												<Card.Body>
													<p><strong>Carrito:</strong></p>
													<ul className="mb-3">
														<li>1 producto <Badge style={getBadgeStyle('G')}>G</Badge></li>
														<li>1 producto <Badge style={getBadgeStyle('M')}>M</Badge></li>
													</ul>
													<p><strong>Cálculo:</strong></p>
													<ul className="mb-3">
														<li>G: 1/{getCatVal('G')} = <strong>{fracStr('G')}</strong></li>
														<li>M: 1/{getCatVal('M')} = <strong>{fracStr('M')}</strong></li>
													</ul>
													<p className="mb-2">Fracción total: {ej2Fracs.toFixed(2)} → ceil = <strong>{bultosTxt(ej2Bultos)}</strong></p>
													<Alert variant="warning" className="mb-0 py-2">
														<strong>Total: {bultosTxt(ej2Bultos)}</strong>
													</Alert>
												</Card.Body>
											</Card>
										</Col>

										{/* Ejemplo 3: 1 GG + 1 M + 1 PPP */}
										<Col md={4} className="mb-3">
											<Card className="h-100 border-danger">
												<Card.Header className="bg-danger text-white">
													<strong>Ejemplo 3:</strong> GG + M + PPP
												</Card.Header>
												<Card.Body>
													<p><strong>Carrito:</strong></p>
													<ul className="mb-3">
														<li>1 producto <Badge style={getBadgeStyle('GG')}>GG</Badge></li>
														<li>1 producto <Badge style={getBadgeStyle('M')}>M</Badge></li>
														<li>1 producto <Badge style={getBadgeStyle('PPP')}>PPP</Badge></li>
													</ul>
													<p><strong>Cálculo:</strong></p>
													<ul className="mb-3">
														<li>GG: 1/{getCatVal('GG')} = <strong>{fracStr('GG')}</strong></li>
														<li>M: 1/{getCatVal('M')} = <strong>{fracStr('M')}</strong></li>
														<li>PPP: 1/{getCatVal('PPP')} = <strong>{fracStr('PPP')}</strong></li>
													</ul>
													<p className="mb-2">Fracción total: {ej3Fracs.toFixed(2)} → ceil = <strong>{bultosTxt(ej3Bultos)}</strong></p>
													<Alert variant="danger" className="mb-0 py-2">
														<strong>Total: {bultosTxt(ej3Bultos)}</strong>
													</Alert>
												</Card.Body>
											</Card>
										</Col>
									</Row>

									<Alert variant="success" className="mt-3 mb-0">
										<i className="fa fa-truck me-2"></i>
										Si el área de envío tiene un precio de <strong>Gs. 25.000 por bulto</strong>:
										<ul className="mb-0 mt-2">
											<li>Ejemplo 1 ({bultosTxt(ej1Bultos)}): <strong>Gs. {(25000 * ej1Bultos).toLocaleString('es-PY')}</strong></li>
											<li>Ejemplo 2 ({bultosTxt(ej2Bultos)}): <strong>Gs. {(25000 * ej2Bultos).toLocaleString('es-PY')}</strong></li>
											<li>Ejemplo 3 ({bultosTxt(ej3Bultos)}): <strong>Gs. {(25000 * ej3Bultos).toLocaleString('es-PY')}</strong></li>
										</ul>
									</Alert>
								</Card.Body>
							</Card>
						</Col>
					</Row>
				)
			})()}
		</Container>
	)
}

export default EnvioBultos
